export interface FormOption {
  value: string;
  label: string;
}

export interface ContactFormConfig {
  lado_etiqueta: string;
  lado_titulo: string;
  lado_descripcion: string;
  titulo: string;
  subtitulo: string;
  boton_enviar: string;
  mensaje_exito: string;
  aviso_legal: string;
  campos: {
    nombre: { label: string; placeholder: string };
    email: { label: string; placeholder: string };
    telefono: { label: string; placeholder: string };
    tipo_unidad: { label: string; placeholder: string; visible: boolean; required: boolean; opciones: FormOption[] };
    m2: { label: string; placeholder: string; visible: boolean; required: boolean; opciones: FormOption[] };
    fecha_inicio: { label: string; visible: boolean; required: boolean };
    mensaje: { label: string; placeholder: string; visible: boolean; required: boolean; max_caracteres: number };
  };
}

export type SavedContactFormConfig = Partial<Omit<ContactFormConfig, "campos">> & {
  campos?: { [K in keyof ContactFormConfig["campos"]]?: Partial<ContactFormConfig["campos"][K]> };
};

// Textos originales del formulario: se usan mientras no se guarde nada desde el admin.
export const DEFAULT_CONTACT_FORM_CONFIG: ContactFormConfig = {
  lado_etiqueta: "Hablemos",
  lado_titulo: "Estamos a un mensaje de distancia",
  lado_descripcion:
    "Preferimos lo simple: escríbenos, coordinamos una visita y vemos juntos la unidad que mejor se adapta a tu operación.",
  titulo: "Solicita una cotización o visita",
  subtitulo: "Completa el formulario y te contactamos a la brevedad.",
  boton_enviar: "Enviar solicitud",
  mensaje_exito: "¡Gracias! Recibimos tu solicitud. Te contactaremos a la brevedad por teléfono o WhatsApp.",
  aviso_legal: "Al enviar aceptas ser contactado por ESPACIO BORMAN. No compartimos tus datos con terceros.",
  campos: {
    nombre: { label: "Nombre", placeholder: "Tu nombre" },
    email: { label: "Email", placeholder: "tu@correo.cl" },
    telefono: { label: "Teléfono", placeholder: "+56 9 ..." },
    tipo_unidad: {
      label: "Tipo de unidad",
      placeholder: "Selecciona una opción",
      visible: true,
      required: true,
      opciones: [
        { value: "Mini bodega", label: "Mini bodega (12 – 40 m²)" },
        { value: "Bodega mediana", label: "Bodega mediana (50 – 300 m²)" },
        { value: "Galpon", label: "Galpón (300 – 2.000+ m²)" },
        { value: "A medida", label: "Espacio a medida" },
        { value: "No estoy seguro", label: "No estoy seguro" },
      ],
    },
    m2: {
      label: "m² aproximados",
      placeholder: "Selecciona un rango",
      visible: true,
      required: true,
      opciones: [
        { value: "12-40", label: "12 – 40 m²" },
        { value: "50-150", label: "50 – 150 m²" },
        { value: "150-300", label: "150 – 300 m²" },
        { value: "300-1000", label: "300 – 1.000 m²" },
        { value: "mas-de-1000", label: "Más de 1.000 m²" },
      ],
    },
    fecha_inicio: { label: "Fecha de inicio estimada", visible: true, required: false },
    mensaje: {
      label: "Mensaje",
      placeholder: "Cuéntanos qué necesitas guardar, para qué lo usarás o cualquier otra información.",
      visible: true,
      required: false,
      max_caracteres: 500,
    },
  },
};

// Los campos nombre, email y telefono siempre se muestran y son obligatorios:
// el servidor los exige para poder contactar a la persona.
export const FIXED_REQUIRED_FIELDS = ["nombre", "email", "telefono"] as const;

function mergeSection<T extends object>(base: T, saved: Partial<T> | undefined): T {
  const out = { ...base } as Record<string, unknown>;
  for (const [key, value] of Object.entries(saved ?? {})) {
    if (value !== undefined && value !== null && typeof value === typeof (base as Record<string, unknown>)[key]) {
      out[key] = value;
    }
  }
  return out as T;
}

export function mergeContactFormConfig(saved?: SavedContactFormConfig | null): ContactFormConfig {
  const base = DEFAULT_CONTACT_FORM_CONFIG;
  if (!saved) return base;

  const { campos: savedCampos, ...savedTop } = saved;
  const top = mergeSection(
    {
      lado_etiqueta: base.lado_etiqueta,
      lado_titulo: base.lado_titulo,
      lado_descripcion: base.lado_descripcion,
      titulo: base.titulo,
      subtitulo: base.subtitulo,
      boton_enviar: base.boton_enviar,
      mensaje_exito: base.mensaje_exito,
      aviso_legal: base.aviso_legal,
    },
    savedTop
  );

  const campos = {
    nombre: mergeSection(base.campos.nombre, savedCampos?.nombre),
    email: mergeSection(base.campos.email, savedCampos?.email),
    telefono: mergeSection(base.campos.telefono, savedCampos?.telefono),
    tipo_unidad: mergeSection(base.campos.tipo_unidad, savedCampos?.tipo_unidad),
    m2: mergeSection(base.campos.m2, savedCampos?.m2),
    fecha_inicio: mergeSection(base.campos.fecha_inicio, savedCampos?.fecha_inicio),
    mensaje: mergeSection(base.campos.mensaje, savedCampos?.mensaje),
  };

  return { ...top, campos };
}
